import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { formatDate } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import type {
  BandEnum,
  ModeratorCompanyReadiness,
  ModeratorReadinessOptionRequest,
  ModeratorReadinessQuestion,
  ModeratorReadinessQuestionRequest,
} from "@/services/moderators/generated/model";
import {
  getModeratorsReadinessCompaniesListQueryKey,
  getModeratorsReadinessQuestionsListQueryKey,
  getModeratorsReadinessSummaryRetrieveQueryKey,
  useModeratorsReadinessCompaniesList,
  useModeratorsReadinessQuestionsCreate,
  useModeratorsReadinessQuestionsDestroy,
  useModeratorsReadinessQuestionsList,
  useModeratorsReadinessQuestionsUpdate,
  useModeratorsReadinessSummaryRetrieve,
} from "@/services/moderators/generated/moderators-readiness/moderators-readiness";
import "./ModeratorPage.css";
import "./ModeratorReadinessPage.css";

type BandFilter = BandEnum | "all" | "none";
const BAND_FILTERS: BandFilter[] = ["all", "ready", "developing", "not_ready", "none"];

// Resolve a band value to its localized text; keeps the (typed) t() keys
// static so we don't have to cast dynamic template strings.
const bandText = (t: TFunction<readonly ["moderator", "common"]>, band: string | null | undefined): string => {
  switch (band) {
    case "ready":
      return t("readiness.bands.ready");
    case "developing":
      return t("readiness.bands.developing");
    case "not_ready":
      return t("readiness.bands.not_ready");
    case "none":
      return t("readiness.bands.none");
    default:
      return band ?? "";
  }
};

const bandClass = (band: BandEnum | null | undefined): string => {
  switch (band) {
    case "ready":
      return "modrd__band modrd__band--ready";
    case "developing":
      return "modrd__band modrd__band--developing";
    case "not_ready":
      return "modrd__band modrd__band--not-ready";
    default:
      return "modrd__band modrd__band--none";
  }
};

/**
 * WP2.1 — Moderator surface for the export-readiness check: question
 * CRUD (super moderators only), a per-company view with band filter,
 * and a summary tile. Mounted at `/moderator/readiness`.
 */
const ModeratorReadinessPage = () => (
  <ModeratorLayout>
    {(moderator) => <ReadinessContent canManage={moderator.role === "SUPERMODERATOR"} />}
  </ModeratorLayout>
);

const emptyOption = (): ModeratorReadinessOptionRequest => ({ text: "", points: 0, order: 0 });

const toRequest = (q: Partial<ModeratorReadinessQuestion>): ModeratorReadinessQuestionRequest => ({
  key: q.key ?? "",
  text: q.text ?? "",
  helpText: q.helpText ?? "",
  improvementTip: q.improvementTip ?? "",
  weight: q.weight ?? 10,
  order: q.order ?? 0,
  isActive: q.isActive ?? true,
  options: (q.options ?? []).map((o) => ({
    text: o.text,
    points: o.points ?? 0,
    order: o.order ?? 0,
  })),
});

const ReadinessContent = ({ canManage }: { canManage: boolean }) => {
  const { t } = useTranslation(["moderator", "common"]);
  const queryClient = useQueryClient();

  const [bandFilter, setBandFilter] = useState<BandFilter>("all");
  const [editing, setEditing] = useState<ModeratorReadinessQuestion | null>(null);
  const [draft, setDraft] = useState<ModeratorReadinessQuestionRequest | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    data: questionsData,
    isLoading: questionsLoading,
    error: questionsError,
  } = useModeratorsReadinessQuestionsList();
  // GET returns an array; the shared schema also lists the POST-create
  // response (single item) so orval widens the response type. Narrow it.
  const questions: ModeratorReadinessQuestion[] = Array.isArray(questionsData)
    ? (questionsData as ModeratorReadinessQuestion[])
    : [];

  const companiesParams = bandFilter === "all" ? undefined : { band: bandFilter };
  const { data: companies = [] } = useModeratorsReadinessCompaniesList(companiesParams);
  const { data: summary } = useModeratorsReadinessSummaryRetrieve();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getModeratorsReadinessQuestionsListQueryKey() });
    queryClient.invalidateQueries({ queryKey: getModeratorsReadinessCompaniesListQueryKey() });
    queryClient.invalidateQueries({ queryKey: getModeratorsReadinessSummaryRetrieveQueryKey() });
  };

  const createMutation = useModeratorsReadinessQuestionsCreate({
    mutation: {
      onSuccess: () => {
        invalidate();
        setDraft(null);
        setEditing(null);
      },
      onError: (err: unknown) => setFormError(getApiErrorMessage(err, t("readiness.errors.save"))),
    },
  });
  const updateMutation = useModeratorsReadinessQuestionsUpdate({
    mutation: {
      onSuccess: () => {
        invalidate();
        setDraft(null);
        setEditing(null);
      },
      onError: (err: unknown) => setFormError(getApiErrorMessage(err, t("readiness.errors.save"))),
    },
  });
  const deleteMutation = useModeratorsReadinessQuestionsDestroy({
    mutation: {
      onSuccess: () => invalidate(),
      onError: (err: unknown) => setFormError(getApiErrorMessage(err, t("readiness.errors.delete"))),
    },
  });

  const openNew = () => {
    setEditing(null);
    setDraft({
      key: "",
      text: "",
      helpText: "",
      improvementTip: "",
      weight: 10,
      order: questions.length,
      isActive: true,
      options: [emptyOption(), emptyOption()],
    });
    setFormError(null);
  };

  const openEdit = (q: ModeratorReadinessQuestion) => {
    setEditing(q);
    setDraft(toRequest(q));
    setFormError(null);
  };

  const handleSave = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!draft) return;
    if (draft.options.length < 2) {
      setFormError(t("readiness.form.needTwoOptions"));
      return;
    }
    setFormError(null);
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: draft });
    } else {
      createMutation.mutate({ data: draft });
    }
  };

  const handleDelete = (q: ModeratorReadinessQuestion) => {
    if (!canManage) return;
    const ok = window.confirm(`${t("readiness.deleteTitle")}\n\n${t("readiness.deleteBody")}`);
    if (!ok) return;
    deleteMutation.mutate({ id: q.id });
  };

  const totalQuestions = questions.length;
  const activeQuestions = questions.filter((q) => q.isActive).length;

  const renderDashboardTile = () => {
    if (!summary) return null;
    const bySummary = ((summary as Record<string, unknown>).byBand ?? []) as { band: string; count: number }[];
    const activated = ((summary as Record<string, unknown>).activatedCompanies ?? 0) as number;
    const noAssessment = ((summary as Record<string, unknown>).companiesWithoutAssessment ?? 0) as number;
    return (
      <section className="modrd__summary">
        <div className="modrd__summary-tile">
          <span className="modrd__summary-label">{t("readiness.dashboard.activatedCompanies")}</span>
          <strong className="modrd__summary-value">{activated}</strong>
          <span className="modrd__muted">{t("readiness.dashboard.activatedHint")}</span>
        </div>
        {bySummary.map((row) => (
          <div key={row.band} className="modrd__summary-tile">
            <span className={bandClass(row.band as BandEnum)}>{bandText(t, row.band)}</span>
            <strong className="modrd__summary-value">{row.count}</strong>
          </div>
        ))}
        <div className="modrd__summary-tile">
          <span className="modrd__summary-label">{t("readiness.dashboard.withoutAssessment")}</span>
          <strong className="modrd__summary-value">{noAssessment}</strong>
        </div>
        <div className="modrd__summary-tile">
          <span className="modrd__summary-label">{t("readiness.dashboard.activeQuestions")}</span>
          <strong className="modrd__summary-value">{activeQuestions}</strong>
          <span className="modrd__muted">
            {t("readiness.activeCount", { active: activeQuestions, total: totalQuestions })}
          </span>
        </div>
      </section>
    );
  };

  return (
    <div className="moderator-page modrd">
      <header className="moderator-page__header">
        <div>
          <h1>{t("readiness.pageTitle")}</h1>
          <p className="moderator-page__subtitle">{t("readiness.pageSubtitle")}</p>
        </div>
        {canManage ? (
          <button type="button" className="modrd__btn modrd__btn--primary" onClick={openNew}>
            {t("readiness.actions.new")}
          </button>
        ) : (
          <span className="modrd__muted">{t("readiness.readOnly")}</span>
        )}
      </header>

      {renderDashboardTile()}

      <section className="modrd__card">
        <header className="modrd__card-header">
          <h2>{t("readiness.pageTitle")}</h2>
          <span className="modrd__muted">
            {t("readiness.activeCount", { active: activeQuestions, total: totalQuestions })}
          </span>
        </header>
        {questionsLoading ? (
          <p>{t("common:states.loading")}</p>
        ) : questionsError ? (
          <p className="modrd__error">{t("readiness.errors.load")}</p>
        ) : (
          <table className="modrd__table">
            <thead>
              <tr>
                <th>{t("readiness.columns.key")}</th>
                <th>{t("readiness.columns.text")}</th>
                <th>{t("readiness.columns.weight")}</th>
                <th>{t("readiness.columns.options")}</th>
                <th>{t("readiness.columns.active")}</th>
                {canManage ? <th>{t("readiness.columns.actions")}</th> : null}
              </tr>
            </thead>
            <tbody>
              {questions.map((q) => (
                <tr key={q.id}>
                  <td className="modrd__mono">{q.key}</td>
                  <td>{q.text}</td>
                  <td>{q.weight ?? 1}</td>
                  <td>{q.options.length}</td>
                  <td>{q.isActive ? "✓" : "—"}</td>
                  {canManage ? (
                    <td className="modrd__actions-cell">
                      <button type="button" className="modrd__btn" onClick={() => openEdit(q)}>
                        {t("readiness.actions.edit")}
                      </button>
                      <button type="button" className="modrd__btn modrd__btn--danger" onClick={() => handleDelete(q)}>
                        {t("readiness.actions.delete")}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="modrd__card">
        <header className="modrd__card-header">
          <h2>{t("readiness.companies.pageTitle")}</h2>
          <label className="modrd__filter">
            <span>{t("readiness.companies.filterLabel")}:</span>
            <select value={bandFilter} onChange={(e) => setBandFilter(e.target.value as BandFilter)}>
              {BAND_FILTERS.map((b) => (
                <option key={b} value={b}>
                  {b === "all"
                    ? t("readiness.companies.all")
                    : b === "none"
                      ? t("readiness.companies.none")
                      : bandText(t, b)}
                </option>
              ))}
            </select>
          </label>
        </header>
        {(companies as ModeratorCompanyReadiness[]).length === 0 ? (
          <p className="modrd__muted">{t("readiness.companies.empty")}</p>
        ) : (
          <table className="modrd__table">
            <thead>
              <tr>
                <th>{t("readiness.companies.columns.company")}</th>
                <th>{t("readiness.companies.columns.score")}</th>
                <th>{t("readiness.companies.columns.band")}</th>
                <th>{t("readiness.companies.columns.status")}</th>
                <th>{t("readiness.companies.columns.takenAt")}</th>
              </tr>
            </thead>
            <tbody>
              {(companies as ModeratorCompanyReadiness[]).map((row) => (
                <tr key={row.companyId}>
                  <td>{row.companyName}</td>
                  <td>{row.score ?? "—"}</td>
                  <td>
                    <span className={bandClass(row.band as BandEnum | null)}>
                      {row.band ? bandText(t, row.band) : t("readiness.bands.none")}
                    </span>
                  </td>
                  <td>{row.passed == null ? "—" : row.passed ? t("readiness.passed") : t("readiness.notPassed")}</td>
                  <td>{row.completedAt ? formatDate(row.completedAt) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {draft ? (
        <div className="modrd__modal-backdrop" role="dialog" aria-modal>
          <form className="modrd__modal" onSubmit={handleSave}>
            <header className="modrd__modal-header">
              <h2>{editing ? t("readiness.form.editTitle") : t("readiness.form.newTitle")}</h2>
            </header>
            <div className="modrd__modal-body">
              <label className="modrd__field">
                <span>{t("readiness.form.key")}</span>
                <input
                  type="text"
                  value={draft.key}
                  onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                  pattern="[-a-zA-Z0-9_]+"
                  required
                />
                <small className="modrd__muted">{t("readiness.form.keyHint")}</small>
              </label>
              <label className="modrd__field">
                <span>{t("readiness.form.text")}</span>
                <textarea
                  value={draft.text}
                  onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                  required
                  rows={2}
                />
              </label>
              <label className="modrd__field">
                <span>{t("readiness.form.helpText")}</span>
                <textarea
                  value={draft.helpText ?? ""}
                  onChange={(e) => setDraft({ ...draft, helpText: e.target.value })}
                  rows={2}
                />
              </label>
              <label className="modrd__field">
                <span>{t("readiness.form.improvementTip")}</span>
                <textarea
                  value={draft.improvementTip ?? ""}
                  onChange={(e) => setDraft({ ...draft, improvementTip: e.target.value })}
                  rows={2}
                />
              </label>
              <div className="modrd__field-row">
                <label className="modrd__field">
                  <span>{t("readiness.form.weight")}</span>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={draft.weight ?? 10}
                    onChange={(e) => setDraft({ ...draft, weight: Number(e.target.value) })}
                  />
                </label>
                <label className="modrd__field">
                  <span>{t("readiness.form.order")}</span>
                  <input
                    type="number"
                    min={0}
                    value={draft.order ?? 0}
                    onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })}
                  />
                </label>
                <label className="modrd__field modrd__field--checkbox">
                  <input
                    type="checkbox"
                    checked={draft.isActive ?? true}
                    onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                  />
                  <span>{t("readiness.form.isActive")}</span>
                </label>
              </div>

              <h3 className="modrd__card-subtitle">{t("readiness.form.options")}</h3>
              <ul className="modrd__option-list">
                {draft.options.map((opt, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: options are ordered and locally edited; identity is positional
                  <li key={idx} className="modrd__option-row">
                    <input
                      type="text"
                      placeholder={t("readiness.form.optionText")}
                      value={opt.text}
                      onChange={(e) => {
                        const next = [...draft.options];
                        next[idx] = { ...opt, text: e.target.value };
                        setDraft({ ...draft, options: next });
                      }}
                      required
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={opt.points ?? 0}
                      aria-label={t("readiness.form.optionPoints")}
                      onChange={(e) => {
                        const next = [...draft.options];
                        next[idx] = { ...opt, points: Number(e.target.value) };
                        setDraft({ ...draft, options: next });
                      }}
                    />
                    <input
                      type="number"
                      min={0}
                      value={opt.order ?? 0}
                      aria-label={t("readiness.form.optionOrder")}
                      onChange={(e) => {
                        const next = [...draft.options];
                        next[idx] = { ...opt, order: Number(e.target.value) };
                        setDraft({ ...draft, options: next });
                      }}
                    />
                    <button
                      type="button"
                      className="modrd__btn modrd__btn--danger"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          options: draft.options.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="modrd__btn"
                onClick={() => setDraft({ ...draft, options: [...draft.options, emptyOption()] })}
              >
                {t("readiness.actions.addOption")}
              </button>
              {formError ? <p className="modrd__error">{formError}</p> : null}
            </div>
            <footer className="modrd__modal-footer">
              <button
                type="button"
                className="modrd__btn"
                onClick={() => {
                  setDraft(null);
                  setEditing(null);
                }}
              >
                {t("readiness.form.cancel")}
              </button>
              <button
                type="submit"
                className="modrd__btn modrd__btn--primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? t("readiness.form.saving")
                  : t("readiness.form.save")}
              </button>
            </footer>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default ModeratorReadinessPage;
